import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { FaUserPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../redux/reducers';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';
import connectionService from '../../../services/community/connectionService';
import communityService from '../../../services/community/communityService';
import { CommunityRole } from '../../../types/community';
import SearchBar from '../forms/SearchBar';
import ModalLayout from '../layouts/ModalLayout';
import ConnectionsList from '../misc/ConnectionsList';
import CancelSaveButton from '../misc/CancelSaveButton';
import ModalHeader from '../misc/ModalHeader';

export interface AddCommunityMemberModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  communityId: number;
  /** When set, only members of this main community can be added (business sub-communities). */
  mainCommunityId?: number;
  existingMemberUserIds?: string[];
  onMembersAdded?: () => void;
}

function hasUserId(connection: Connection): connection is Connection & { userId: number } {
  return connection.userId != null;
}

function isMemberWithUserId(
  connection: Connection,
): connection is Connection & { userId: number } {
  return connection.type === ConnectionType.MEMBER && hasUserId(connection);
}

export default function AddCommunityMemberModal({
  open,
  setOpen,
  communityId,
  mainCommunityId,
  existingMemberUserIds = [],
  onMembersAdded,
}: AddCommunityMemberModalProps): JSX.Element {
  const { t } = useTranslation();
  const connections = useSelector((state: RootState) => state.connections);

  const [userIds, setUserIds] = useState<string[]>([]);
  const [search, setSearch] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [mainCommunityMembers, setMainCommunityMembers] = useState<Connection[]>([]);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    if (!connections.length) connectionService.getConnections();
  }, [open, connections.length]);

  useEffect(() => {
    if (!open) {
      setUserIds([]);
      setSearch('');
      return;
    }

    if (mainCommunityId) {
      communityService
        .getCommunityMembers(mainCommunityId)
        .then(setMainCommunityMembers)
        .catch((error) => {
          console.error('Failed to fetch main community members:', error);
        });
    } else {
      setMainCommunityMembers([]);
    }
  }, [open, mainCommunityId]);

  const listConnections = useMemo(() => {
    const isAlreadyMember = (userId: number) => existingMemberUserIds.includes(String(userId));

    const source = mainCommunityId
      ? mainCommunityMembers.filter(hasUserId)
      : connections.filter(isMemberWithUserId);

    return source
      .map((member) => {
        const { userId } = member;
        if (isAlreadyMember(userId)) return null;
        return {
          id: String(userId),
          alias: `${member.fields.FIRST_NAME}+${member.fields.LAST_NAME}`,
          disabled: false,
        };
      })
      .filter((entry): entry is { id: string; alias: string; disabled: boolean } => entry != null);
  }, [connections, existingMemberUserIds, mainCommunityId, mainCommunityMembers]);

  const handleSubmit = async () => {
    if (userIds.length === 0) {
      toast.error(t('component.modal.addCommunityMember.error.noUsersSelected'));
      return;
    }

    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    const newUserIds = userIds.filter((userId) => !existingMemberUserIds.includes(userId));

    if (newUserIds.length === 0) {
      toast.error(t('component.modal.addCommunityMember.error.allAlreadyMembers'));
      isSubmittingRef.current = false;
      return;
    }

    setLoading(true);
    try {
      await communityService.addMembersDirectly(
        communityId,
        newUserIds.map((userId) => parseInt(userId, 10)),
        CommunityRole.MEMBER,
      );

      toast.success(
        t('component.modal.addCommunityMember.success', { count: newUserIds.length }),
      );

      if (onMembersAdded) {
        onMembersAdded();
      }

      setOpen(false);
    } catch (error) {
      console.error('Failed to add members:', error);
      if (axios.isAxiosError(error)) {
        const apiMessage = error.response?.data?.message as string | undefined;
        if (apiMessage === 'User must be a member of the main community of the parent business') {
          toast.error(t('component.modal.addCommunityMember.error.notMainCommunityMember'));
        } else if (apiMessage) {
          toast.error(apiMessage);
        } else {
          toast.error(t('component.modal.addCommunityMember.error.failed'));
        }
      } else {
        toast.error(t('component.modal.addCommunityMember.error.failed'));
      }
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton={!loading}
      className="z-50 mx-6 flex w-fit min-w-[450px] max-w-[650px] flex-col gap-[20px] rounded-[12px] bg-secondary-50 px-10 py-6 pb-4 pt-5">
      <ModalHeader
        icon={FaUserPlus}
        title={t('component.modal.addCommunityMember.title')}
        subtitle={
          mainCommunityId
            ? t('component.modal.addCommunityMember.subtitleSubCommunity')
            : t('component.modal.addCommunityMember.subtitle')
        }
      />

      <div className="w-full border-b border-primary-300" />

      <h2 className="text-[22px] leading-[24px]">
        {t('component.modal.addCommunityMember.selectMembers')}
      </h2>

      <SearchBar search={search} setSearch={setSearch} />

      <ConnectionsList
        selected={userIds}
        setSelected={setUserIds}
        hasSearch={false}
        className="border-none py-0"
        search={search}
        connections={listConnections}
      />

      <div className="w-full border-b border-primary-300" />

      <div className="flex flex-col items-end gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p>
          {userIds.length} {t('component.modal.addCommunityMember.selected')}
        </p>
        <CancelSaveButton
          buttonText={t('component.modal.addCommunityMember.addButton')}
          onCancel={() => setOpen(false)}
          disabled={userIds.length === 0 || loading}
          onSave={handleSubmit}
        />
      </div>
    </ModalLayout>
  );
}
